import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, History, MessageSquarePlus, Pencil, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { canAccessInvoicing, hasPermission } from '@/lib/permissions';
import { formatDateTime } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { AI_EXTRAS, FEATURE_STATUS } from '@/lib/integrations';
import { useFeature } from '@/hooks/useIntegrationStatus';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { RowActions } from '@/components/ui/row-actions';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import AiStatusPill from '@/components/documents-ai/AiStatusPill';
import AiHistory from '@/components/documents-ai/AiHistory';
import MorningBriefCard from '@/components/documents-ai/MorningBriefCard';
import AiComposer, { PromptLibrary } from '@/components/documents-ai/AiComposer';
import { PROPOSAL_TYPES } from '@/components/documents-ai/AiActionCard';
import { useProjectList } from '@/components/documents-ai/ProjectPicker';
import { AssistantMessage, AssistantTile, UserMessage } from '@/components/documents-ai/AiMessage';
import {
  QUICK_PROMPTS, chatToText, friendlyAiError, placeholderRange, promptLibraryFor,
} from '@/components/documents-ai/aiModel';
import { saveText, useElementHeight, useMediaQuery } from '@/components/documents-ai/ui';

let localIdCounter = 0;
const localId = (prefix) => `${prefix}-local-${Date.now()}-${(localIdCounter += 1)}`;
// The chat card's height — the side column (Morning brief + History) uses the same, so neither runs past the
// other. On a phone the bottom tab bar (--app-bottom-nav-height, set by Layout) is taken off, so the composer
// and its note are never hidden behind it.
const CHAT_HEIGHT = 'h-[calc(100dvh_-_14rem_-_var(--app-bottom-nav-height,0px))] min-h-[420px] md:min-h-[540px]';
// The side column shares that height: the Morning brief takes only whole lines that leave History room for about
// four chats (two date headings, the search box and "Show N older" included); opened in full, the brief may
// leave History its minimum. SIDE_GAP is the column's gap-4.
const SIDE_GAP = 16;
const HISTORY_ROOM = 380;
const HISTORY_MIN = 200;
// Messages the server stored (have a real id) can take feedback and undo.
const isStored = (message) => Boolean(message?.id) && !String(message.id).includes('-local-');

const isCancelled = (error) => error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError';

/** The project a chat was last focused on (the newest question asked with "@project"). */
const lastScopeOf = (messages) => {
  const asked = [...(messages || [])].reverse().find((item) => item.role === 'user' && item.scope?.project_id);
  return asked ? { id: asked.scope.project_id, name: asked.scope.project_name } : null;
};

const actionToastText = (action) => {
  if (action?.type === 'task_created') return `Task “${action.task?.name || 'New task'}” created`;
  if (action?.type === 'invoice_updated') return `Invoice set to ${action.status}`;
  return 'Change saved';
};

function ChatTitle({ title, createdAt, editing, onSave, onCancel }) {
  const [value, setValue] = useState(title);
  const inputRef = useRef(null);
  useEffect(() => {
    if (editing) {
      setValue(title);
      window.setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 0);
    }
  }, [editing, title]);

  if (editing) {
    const save = () => {
      const next = value.trim();
      if (!next || next === title) onCancel();
      else onSave(next);
    };
    return (
      <input
        ref={inputRef}
        value={value}
        maxLength={80}
        aria-label="Chat name"
        data-testid="ai-chat-title-input"
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') { event.preventDefault(); save(); }
          if (event.key === 'Escape') { event.preventDefault(); onCancel(); }
        }}
        onBlur={save}
        className="h-8 min-w-0 flex-1 rounded-lg border border-[color:var(--brand-ink)] bg-surface px-2 text-[14.5px] font-bold text-ink outline-none ring-2 ring-[color:var(--brand-soft-border)]"
      />
    );
  }
  return (
    <h2 className="min-w-0 truncate text-[14.5px] font-bold text-ink" data-testid="ai-chat-title">
      {title}
      {createdAt ? <span className="font-normal text-[12.5px] text-ink-3"> · {formatDateTime(createdAt, { relativeDay: true, year: 'auto' })}</span> : null}
    </h2>
  );
}

export default function AiPage() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatParam = searchParams.get('chat') || '';
  const isNarrow = useMediaQuery('(max-width: 1023px)');
  const [sideColumnRef, sideHeight] = useElementHeight();
  const allowed = hasPermission(user, 'ai');
  const invoicing = canAccessInvoicing(user);
  const canAssign = hasPermission(user, 'tasks');

  const [status, setStatus] = useState(null);
  // pinned questions are re-asked only while AI extras is on (Admin › Integrations, default off)
  const aiExtras = useFeature(AI_EXTRAS);
  const [statusError, setStatusError] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState('');
  const [sessionInfo, setSessionInfo] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [scope, setScope] = useState(null);
  const [projectsWanted, setProjectsWanted] = useState(false);
  // Pinned questions (25 idea 10): answer message id → pin id, for the "Pinned" state of the button.
  const [pins, setPins] = useState({});
  const projectList = useProjectList('/ai/projects', projectsWanted);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const pendingRef = useRef(null);
  // The Undo toast of each confirmed change, so Undo on the card also closes it (no second Undo).
  const toastIdsRef = useRef({});
  const scrollRef = useRef(null);
  const currentIdRef = useRef('');
  const loadingIdRef = useRef('');
  currentIdRef.current = currentSessionId;

  const library = useMemo(() => promptLibraryFor({ invoicing }), [invoicing]);
  const quickPrompts = useMemo(
    () => QUICK_PROMPTS.filter((text) => library.some((group) => group.prompts.some((prompt) => prompt.text === text))),
    [library]
  );

  const fetchSessions = useCallback(async () => {
    try {
      const response = await api.get('/ai/sessions');
      setSessions(Array.isArray(response.data) ? response.data : []);
      setHistoryError(false);
    } catch {
      setHistoryError(true);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!allowed) return;
    fetchSessions();
    api.get('/ai/status')
      .then((response) => setStatus(response.data || null))
      .catch(() => setStatusError(true));
    api.get('/ai/pins')
      .then((response) => {
        const items = Array.isArray(response.data?.items) ? response.data.items : [];
        setPins(Object.fromEntries(items.filter((pin) => pin.message_id).map((pin) => [pin.message_id, pin.id])));
      })
      .catch(() => {});
  }, [allowed, fetchSessions]);

  const resetChat = useCallback(() => {
    setCurrentSessionId('');
    setSessionInfo(null);
    setMessages([]);
    setEditingTitle(false);
    setScope(null);
  }, []);

  const loadSession = useCallback(async (sessionId) => {
    loadingIdRef.current = sessionId;
    setSessionLoading(true);
    try {
      const response = await api.get(`/ai/sessions/${encodeURIComponent(sessionId)}`);
      if (loadingIdRef.current !== sessionId) return; // another chat was opened meanwhile
      const session = response.data || {};
      if (!session.id) throw new Error('Chat not found');
      setCurrentSessionId(session.id);
      setSessionInfo({ id: session.id, title: session.title, created_at: session.created_at });
      const loaded = Array.isArray(session.messages) ? session.messages : [];
      setMessages(loaded);
      setScope(lastScopeOf(loaded));
      setEditingTitle(false);
    } catch (err) {
      if (loadingIdRef.current !== sessionId) return;
      notify.error("Couldn't open that chat", { description: getErrorMessage(err) });
      resetChat();
      setSearchParams({}, { replace: true });
    } finally {
      if (loadingIdRef.current === sessionId) {
        loadingIdRef.current = '';
        setSessionLoading(false);
      }
    }
  }, [resetChat, setSearchParams]);

  // Quiet refresh (after a refused Confirm or a late Stop): the true state from the server, no skeleton.
  const refreshMessages = useCallback(async (sessionId) => {
    try {
      const response = await api.get(`/ai/sessions/${encodeURIComponent(sessionId)}`);
      if (currentIdRef.current === sessionId && Array.isArray(response.data?.messages)) setMessages(response.data.messages);
    } catch {
      // the card keeps showing the error
    }
  }, []);

  /*
    Deep links from other pages: /ai?ask=<question> puts the question in the box (it is NOT sent — the person reads
    it first) and &project=<id> focuses the chat on that project (04 #9 "Ask a follow-up" on the project page, the
    Dashboard brief's "Ask AI"). The parameters then leave the URL, so a reload or Back does not fill the box again.
  */
  const askParam = searchParams.get('ask') || '';
  const projectParam = searchParams.get('project') || '';
  useEffect(() => {
    if (!allowed || chatParam || (!askParam && !projectParam)) return;
    if (askParam) setInput(askParam.slice(0, 2000));
    if (projectParam) {
      setScope({ id: projectParam, name: '' });
      setProjectsWanted(true); // the project's name for the focus chip
    }
    const next = new URLSearchParams(searchParams);
    next.delete('ask');
    next.delete('project');
    setSearchParams(next, { replace: true });
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [allowed, chatParam, askParam, projectParam, searchParams, setSearchParams]);
  // A project focus from a link has only the id: show its name once the project list is in.
  useEffect(() => {
    if (!scope?.id || scope.name || !projectList.projects) return;
    const match = projectList.projects.find((project) => project.id === scope.id);
    setScope(match ? { id: match.id, name: match.name } : null); // not one of the person's projects: no focus
  }, [scope, projectList.projects]);

  // The open chat lives in the URL (?chat=…): it survives reloads and the page remount after an undo.
  useEffect(() => {
    if (!allowed) return;
    if (chatParam && chatParam !== currentIdRef.current && chatParam !== loadingIdRef.current) loadSession(chatParam);
    if (!chatParam && (currentIdRef.current || loadingIdRef.current)) {
      loadingIdRef.current = '';
      setSessionLoading(false);
      resetChat();
    }
  }, [allowed, chatParam, loadSession, resetChat]);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    // Newest message in view; an empty chat starts at the top (welcome + prompt library).
    node.scrollTop = messages.length ? node.scrollHeight : 0;
  }, [messages.length, sending, sessionLoading]);

  if (!allowed) {
    return <div className="p-8 text-ink-3">Only Super Admin has access to AI.</div>;
  }

  const sessionTitle = sessionInfo?.title
    || sessions.find((item) => item.id === currentSessionId)?.title
    || 'New chat';

  const openSession = (sessionId) => {
    if (!sessionId || sending) return;
    setHistoryOpen(false);
    if (sessionId === currentSessionId) return;
    setSearchParams({ chat: sessionId });
  };

  const startNewChat = () => {
    if (sending) return;
    setScope(null);
    setHistoryOpen(false);
    resetChat();
    setInput('');
    setSearchParams({});
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const fillPrompt = (text) => {
    setInput(text);
    window.setTimeout(() => {
      const node = inputRef.current;
      if (!node) return;
      node.focus();
      const range = placeholderRange(text);
      if (range) node.setSelectionRange(range[0], range[1]);
      else node.setSelectionRange(text.length, text.length);
    }, 0);
  };

  const undoAction = async (message, index, sessionId = currentIdRef.current, { fromToast = false } = {}) => {
    const response = await api.post(
      `/ai/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(message.id)}/actions/${index}/undo`
    );
    const nextAction = response.data?.action;
    const toastKey = `${message.id}:${index}`;
    if (!fromToast && toastIdsRef.current[toastKey]) {
      notify.dismiss(toastIdsRef.current[toastKey]);
      delete toastIdsRef.current[toastKey];
    }
    setMessages((current) => current.map((item) => {
      if (item.id !== message.id) return item;
      const actions = [...(item.actions || [])];
      actions[index] = nextAction || { ...actions[index], undone_at: new Date().toISOString() };
      return { ...item, actions };
    }));
    return response.data;
  };

  // clientId identifies this question to the server; "Try again" re-sends the same one, so a question
  // that was answered (or a change that was saved) while the response got lost is returned, not re-run.
  const sendMessage = async (text = input, { clientId } = {}) => {
    const question = String(text || '').trim();
    if (!question || sending) return;
    const userMessage = {
      id: localId('user'),
      role: 'user',
      content: question,
      created_at: new Date().toISOString(),
      local: true,
      scope: scope ? { project_id: scope.id, project_name: scope.name } : undefined,
    };
    const questionId = clientId || userMessage.id;
    const controller = new AbortController();
    abortRef.current = controller;
    pendingRef.current = { clientId: questionId, userId: userMessage.id };
    setInput('');
    setSending(true);
    setMessages((current) => [...current.filter((item) => !item.error), userMessage]);
    try {
      const response = await api.post(
        '/ai/chat',
        { message: question, session_id: currentSessionId || undefined, client_id: questionId, project_id: scope?.id || undefined },
        { signal: controller.signal }
      );
      const data = response.data || {};
      if (data.cancelled) {
        setMessages((current) => current.map((item) => (item.id === userMessage.id ? { ...item, stopped: true } : item)));
        return;
      }
      const assistant = {
        id: data.message_id || localId('assistant'),
        role: 'assistant',
        content: data.answer || 'Done.',
        actions: Array.isArray(data.actions) ? data.actions : [],
        provider: data.provider || '',
        model: data.model || '',
        sources: Array.isArray(data.sources) ? data.sources : [],
        project_refs: data.project_refs || {},
        created_at: data.created_at || new Date().toISOString(),
      };
      setMessages((current) => [
        ...current.map((item) => (item.id === userMessage.id
          ? { ...item, id: data.user_message?.id || item.id, created_at: data.user_message?.created_at || item.created_at, scope: data.user_message?.scope || item.scope }
          : item)),
        assistant,
      ]);
      if (data.session_id) {
        if (data.session_id !== currentSessionId) {
          setCurrentSessionId(data.session_id);
          currentIdRef.current = data.session_id;
          setSessionInfo({ id: data.session_id, title: data.session_title || question, created_at: userMessage.created_at });
          setSearchParams({ chat: data.session_id }, { replace: !currentSessionId });
        }
      }
      fetchSessions();
      // Changes get a toast with Undo too (the card keeps its own Undo). A replayed answer (Try again after
      // a lost response) says the change was already saved; one undone meanwhile gets no toast.
      assistant.actions.forEach((action, index) => {
        // A proposal changes nothing yet: its card asks to confirm. Results (replayed answers) get a toast.
        if (action?.undone_at || PROPOSAL_TYPES.has(action?.type)) return;
        notify.success(data.replayed ? `Already saved: ${actionToastText(action)}` : actionToastText(action), {
          undo: isStored(assistant) && action.undo_id ? () => undoAction(assistant, index, data.session_id) : undefined,
        });
      });
    } catch (error) {
      if (controller.signal.aborted || isCancelled(error)) return; // Stop: handled by stopAnswer
      const friendly = friendlyAiError(error);
      setMessages((current) => [...current, {
        id: localId('error'),
        role: 'assistant',
        error: true,
        errorTitle: friendly.title,
        content: friendly.text,
        retry: friendly.retry,
        question,
        clientId: questionId,
      }]);
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setSending(false);
      }
    }
  };

  // Stop (button or Esc): stop waiting now, and tell the server not to save the answer it is still writing.
  const stopAnswer = async () => {
    const controller = abortRef.current;
    const pending = pendingRef.current;
    if (!controller || !pending) return;
    abortRef.current = null;
    controller.abort();
    setSending(false);
    setMessages((current) => current.map((item) => (item.id === pending.userId ? { ...item, stopped: true } : item)));
    try {
      const response = await api.post('/ai/chat/cancel', { client_id: pending.clientId });
      if (response.data?.answered && response.data.session_id) {
        notify.info('It had already answered', { description: 'The answer is shown in the chat.' });
        if (response.data.session_id === currentIdRef.current) await refreshMessages(response.data.session_id);
        else setSearchParams({ chat: response.data.session_id });
      }
    } catch {
      // Nothing more to do: this page already stopped waiting.
    }
  };

  // "Ask again" on a stopped question or on a change that could not be saved: back into the box.
  const askAgain = (textOrMessage) => {
    const stopped = typeof textOrMessage === 'object' && textOrMessage?.stopped;
    const text = typeof textOrMessage === 'string' ? textOrMessage : textOrMessage?.content;
    if (stopped) {
      setMessages((current) => current.filter((item) => item.id !== textOrMessage.id));
      if (textOrMessage.scope?.project_id) setScope({ id: textOrMessage.scope.project_id, name: textOrMessage.scope.project_name });
    }
    fillPrompt(String(text || ''));
  };

  const replaceAction = (messageId, index, action, content) => {
    setMessages((current) => current.map((item) => {
      if (item.id !== messageId) return item;
      const actions = [...(item.actions || [])];
      actions[index] = action;
      return { ...item, actions, ...(content ? { content } : {}) };
    }));
  };

  // "Confirm update" / "Create task" on a Confirm change card (25 idea 3).
  const confirmAction = async (message, index) => {
    const sessionId = currentIdRef.current;
    try {
      const response = await api.post(
        `/ai/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(message.id)}/actions/${index}/confirm`
      );
      const { action, content, already_confirmed: already } = response.data || {};
      if (!action) return;
      replaceAction(message.id, index, action, content);
      if (!already) {
        toastIdsRef.current[`${message.id}:${index}`] = notify.success(actionToastText(action), {
          undo: action.undo_id ? () => undoAction(message, index, sessionId, { fromToast: true }) : undefined,
        });
      }
      fetchSessions();
    } catch (err) {
      // The card shows the error; a refused change (409) also gets its true state from the server.
      if (err?.response?.status === 409) await refreshMessages(sessionId);
      throw err;
    }
  };

  const cancelAction = async (message, index) => {
    const sessionId = currentIdRef.current;
    const response = await api.post(
      `/ai/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(message.id)}/actions/${index}/cancel`
    );
    if (response.data?.action) replaceAction(message.id, index, response.data.action);
  };

  const retryLast = () => {
    const failed = [...messages].reverse().find((item) => item.error);
    if (!failed?.question) return;
    // Drop the failed question + error, then ask again.
    setMessages((current) => {
      const withoutError = current.filter((item) => item.id !== failed.id);
      const lastUser = [...withoutError].reverse().find((item) => item.role === 'user' && item.local && item.content === failed.question);
      return lastUser ? withoutError.filter((item) => item.id !== lastUser.id) : withoutError;
    });
    window.setTimeout(() => sendMessage(failed.question, { clientId: failed.clientId }), 0);
  };

  const renameSession = async (session, title) => {
    try {
      const response = await api.put(`/ai/sessions/${encodeURIComponent(session.id)}`, { title });
      const saved = response.data || {};
      setSessions((current) => current.map((item) => (item.id === session.id ? { ...item, title: saved.title || title } : item)));
      if (session.id === currentSessionId) setSessionInfo((info) => ({ ...(info || {}), title: saved.title || title }));
      notify.success('Chat renamed');
      return true;
    } catch (err) {
      notify.error('Could not rename the chat', { description: getErrorMessage(err) });
      return false;
    }
  };

  const deleteSession = async (session) => {
    const count = Number(session.message_count) || 0;
    const ok = await confirm({
      title: `Delete “${session.title || 'New chat'}”?`,
      subtitle: count ? `${count} ${count === 1 ? 'message' : 'messages'}` : undefined,
      description: 'The chat is removed from your history. Tasks or invoice changes made from it stay as they are.',
      note: 'You can undo this right after.',
      tone: 'danger',
      icon: Trash2,
      confirmLabel: 'Delete chat',
      onConfirm: async () => {
        await api.delete(`/ai/sessions/${encodeURIComponent(session.id)}`);
      },
    });
    if (!ok) return;
    const wasOpen = session.id === currentSessionId;
    setSessions((current) => current.filter((item) => item.id !== session.id));
    if (wasOpen) {
      resetChat();
      setSearchParams({});
    }
    notify.success(`Chat “${session.title || 'New chat'}” deleted`, {
      undo: async () => {
        await api.post(`/ai/sessions/${encodeURIComponent(session.id)}/restore`);
        await fetchSessions();
        // Put the chat back on screen if it was the open one (and nothing else was opened since).
        if (wasOpen && !currentIdRef.current) setSearchParams({ chat: session.id });
      },
      undoneMessage: 'Chat restored',
    });
    fetchSessions();
  };

  const setFeedback = async (message, value) => {
    const response = await api.put(
      `/ai/sessions/${encodeURIComponent(currentSessionId)}/messages/${encodeURIComponent(message.id)}/feedback`,
      { value }
    );
    const saved = response.data?.feedback ?? value;
    setMessages((current) => current.map((item) => (item.id === message.id ? { ...item, feedback: saved } : item)));
    if (saved) notify.info(saved === 'up' ? 'Thanks — marked as a good answer' : 'Thanks — marked as a bad answer');
  };

  const togglePin = async (message, isPinned) => {
    if (isPinned) {
      const pinId = pins[message.id];
      try {
        await api.delete(`/ai/pins/${encodeURIComponent(pinId)}`);
        setPins((current) => {
          const next = { ...current };
          delete next[message.id];
          return next;
        });
        notify.success('Removed from your Dashboard', {
          undo: async () => {
            await api.post(`/ai/pins/${encodeURIComponent(pinId)}/restore`);
            setPins((current) => ({ ...current, [message.id]: pinId }));
          },
          undoneMessage: 'Pinned again',
        });
      } catch (err) {
        notify.error('Could not unpin', { description: getErrorMessage(err) });
      }
      return;
    }
    try {
      const response = await api.post('/ai/pins', { session_id: currentIdRef.current, message_id: message.id });
      const pin = response.data || {};
      setPins((current) => ({ ...current, [message.id]: pin.id }));
      if (pin.already_pinned) {
        notify.info('Already on your Dashboard', { description: 'This question is pinned from another answer.' });
        return;
      }
      notify.success('Pinned to your Dashboard', {
        description: aiExtras.enabled
          ? 'The question is answered again every morning.'
          : aiExtras.status === FEATURE_STATUS.OFF
            ? 'The answer is kept. It refreshes every morning once an admin switches on AI extras in Admin › Integrations.'
            : 'The answer is kept; it refreshes every morning once the AI is set up.',
        undo: async () => {
          await api.delete(`/ai/pins/${encodeURIComponent(pin.id)}`);
          setPins((current) => {
            const next = { ...current };
            delete next[message.id];
            return next;
          });
        },
        undoneMessage: 'Unpinned',
      });
    } catch (err) {
      notify.error('Could not pin this answer', { description: getErrorMessage(err) });
    }
  };

  const exportChat = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    const safeTitle = String(sessionTitle).replace(/[^\w\- ]+/g, '').trim().replace(/\s+/g, '-').slice(0, 40) || 'chat';
    saveText(chatToText(sessionTitle, messages), `ai-${safeTitle}-${stamp}.txt`, 'text/plain;charset=utf-8');
  };

  const currentSession = sessions.find((item) => item.id === currentSessionId) || (currentSessionId ? { id: currentSessionId, title: sessionTitle } : null);
  const hasMessages = messages.length > 0;
  const firstName = String(user?.name || '').split(/\s+/)[0];
  const actionsNote = 'AI reads the project, invoice, task, procurement and stock data you can access. Changes always ask first.';

  const historyPanel = (
    <AiHistory
      sessions={sessions}
      loading={historyLoading}
      error={historyError}
      currentId={currentSessionId}
      disabled={sending}
      onOpen={openSession}
      onRename={renameSession}
      onDelete={deleteSession}
      onRetry={fetchSessions}
      className={isNarrow ? 'h-full border-0 p-0 shadow-none' : 'min-h-[200px] flex-1'}
    />
  );
  // Beside the chat the brief shows the whole lines that fit above about four chats of History, and folds the
  // rest behind "Show N more" (25 idea 9, QA fix for 768 px-tall laptops).
  const morningBrief = (
    <MorningBriefCard
      onAsk={fillPrompt}
      fit={!isNarrow}
      maxHeight={!isNarrow && sideHeight ? Math.max(0, sideHeight - SIDE_GAP - HISTORY_ROOM) : 0}
      openMaxHeight={!isNarrow && sideHeight ? Math.max(0, sideHeight - SIDE_GAP - HISTORY_MIN) : 0}
      className={isNarrow ? 'shadow-none' : undefined}
    />
  );

  return (
    <div data-testid="ai-page" className="space-y-5">
      <PageHeader
        title="AI"
        className="max-sm:flex-col max-sm:items-stretch max-sm:gap-y-2.5"
        subtitle={isNarrow
          ? 'Answers come from live Sara data.'
          : 'Ask about projects, invoices, tasks, procurement and stock — answers come from live Sara data.'}
        actions={(
          <>
            <AiStatusPill status={status} error={statusError} />
            <Button type="button" onClick={startNewChat} disabled={sending} data-testid="ai-new-chat">
              <MessageSquarePlus aria-hidden="true" />
              New chat
            </Button>
          </>
        )}
      />

      <div className="grid items-start gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
        {!isNarrow ? (
          // Left column: the Morning brief (25 idea 9) above History, exactly as tall as the chat card beside it.
          <div ref={sideColumnRef} className={cn('flex min-h-0 flex-col gap-4', CHAT_HEIGHT)} data-testid="ai-side-column">
            {morningBrief}
            {historyPanel}
          </div>
        ) : null}

        <section
          className={cn('flex min-w-0 flex-col rounded-card border border-line bg-surface shadow-card', CHAT_HEIGHT)}
          aria-label="Chat"
          data-testid="ai-chat"
        >
          <header className="flex h-[52px] shrink-0 items-center gap-2 border-b border-line px-4 sm:px-5">
            {isNarrow ? (
              <Button type="button" variant="outline" size="sm" onClick={() => setHistoryOpen(true)} data-testid="ai-open-history">
                <History aria-hidden="true" />
                History
              </Button>
            ) : null}
            <ChatTitle
              title={sessionTitle}
              createdAt={currentSessionId ? (sessionInfo?.created_at || currentSession?.created_at) : ''}
              editing={editingTitle && Boolean(currentSession)}
              onCancel={() => setEditingTitle(false)}
              onSave={async (title) => {
                const ok = await renameSession(currentSession, title);
                if (ok) setEditingTitle(false);
              }}
            />
            <div className="ml-auto flex shrink-0 items-center gap-0.5">
              {currentSession ? (
                <Button type="button" variant="ghost" size="icon-xs" className="text-ink-3" aria-label="Rename chat" title="Rename chat" onClick={() => setEditingTitle(true)}>
                  <Pencil aria-hidden="true" />
                </Button>
              ) : null}
              {hasMessages ? (
                <Button type="button" variant="ghost" size="icon-xs" className="text-ink-3" aria-label="Download chat" title="Download chat as text" onClick={exportChat}>
                  <Download aria-hidden="true" />
                </Button>
              ) : null}
              {currentSession ? (
                <RowActions
                  label="Chat actions"
                  items={[
                    { label: 'Rename', icon: Pencil, onSelect: () => setEditingTitle(true) },
                    hasMessages && { label: 'Download as text', icon: Download, onSelect: exportChat },
                    { label: 'Delete chat…', icon: Trash2, danger: true, onSelect: () => deleteSession(currentSession) },
                  ]}
                />
              ) : null}
            </div>
          </header>

          <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-7 sm:py-6" data-testid="ai-messages" aria-live="polite">
            {sessionLoading ? (
              <div className="space-y-4" aria-label="Loading chat">
                <div className="ml-auto h-10 w-2/5 rounded-xl bg-surface-2 ui-skeleton-shimmer" />
                <div className="h-24 w-3/5 rounded-xl bg-surface-2 ui-skeleton-shimmer" />
              </div>
            ) : !hasMessages ? (
              <div className="mx-auto max-w-[760px]" data-testid="ai-welcome">
                <div className="flex gap-3">
                  <AssistantTile />
                  <div>
                    <p className="text-[15px] font-bold text-ink">{firstName ? `Hi ${firstName} — what do you need to know?` : 'What do you need to know?'}</p>
                    <p className="mt-1 text-[13px] leading-[1.55] text-ink-3">
                      Ask about projects, invoices, tasks, procurement or stock. Pick a question below to start — it goes into the box so you can edit it before sending.
                    </p>
                  </div>
                </div>
                {isNarrow ? <div className="mt-5">{morningBrief}</div> : null}
                <div className="mt-5">
                  <PromptLibrary library={library} onPick={fillPrompt} variant="cards" />
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                {messages.map((message) => (message.role === 'user' ? (
                  <UserMessage key={message.id || message.content} message={message} userName={user?.name} onAskAgain={askAgain} />
                ) : (
                  <AssistantMessage
                    key={message.id || message.content}
                    message={message}
                    canAssign={canAssign}
                    onFeedback={currentSessionId && isStored(message) ? setFeedback : undefined}
                    onUndoAction={currentSessionId && isStored(message) ? undoAction : undefined}
                    onConfirmAction={confirmAction}
                    onCancelAction={cancelAction}
                    onAskAgain={askAgain}
                    onRetry={message.error ? retryLast : undefined}
                    onPin={currentSessionId && isStored(message) ? togglePin : undefined}
                    pinned={Boolean(pins[message.id])}
                  />
                )))}
                {sending ? (
                  <div className="flex gap-3" data-testid="ai-thinking">
                    <AssistantTile />
                    <div className="min-w-0 flex-1 pt-1">
                      <p className="flex items-center gap-2 text-[13px] text-ink-3">
                        <span className="inline-flex gap-1" aria-hidden="true">
                          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[color:var(--brand-ink)]" />
                          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[color:var(--brand-ink)] [animation-delay:150ms]" />
                          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[color:var(--brand-ink)] [animation-delay:300ms]" />
                        </span>
                        {scope ? <>Reading <b className="font-semibold text-ink-2">{scope.name}</b> and writing the answer…</> : 'Reading live Sara data and writing the answer…'}
                      </p>
                      <div className="mt-2.5 h-2.5 w-2/3 max-w-[480px] rounded-full bg-surface-2 ui-skeleton-shimmer" />
                      <div className="mt-2 h-2.5 w-2/5 max-w-[320px] rounded-full bg-surface-2 ui-skeleton-shimmer" />
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <AiComposer
            ref={inputRef}
            value={input}
            onChange={setInput}
            onSend={() => sendMessage()}
            onStop={stopAnswer}
            sending={sending}
            disabled={sessionLoading}
            library={library}
            quickPrompts={quickPrompts}
            showChips={hasMessages}
            onPick={fillPrompt}
            placeholder={scope ? `Ask about ${scope.name}…` : 'Ask about projects, invoices, tasks, stock… type @ to focus on a project'}
            note={actionsNote}
            scope={scope}
            onScopeChange={(next) => setScope(next ? { id: next.id, name: next.name } : null)}
            projectList={projectList}
            onWantProjects={() => setProjectsWanted(true)}
          />
        </section>
      </div>

      {isNarrow ? (
        <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
          <SheetContent side="left" className="flex w-[88vw] max-w-[340px] flex-col gap-0 border-line bg-surface p-4">
            <SheetTitle className="sr-only">Chat history</SheetTitle>
            <SheetDescription className="sr-only">Open, rename or delete a past chat</SheetDescription>
            {historyPanel}
          </SheetContent>
        </Sheet>
      ) : null}
    </div>
  );
}
